import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormSubmitButton, FormCancelButton, ErrorAlert, FormSelectField, FormField, withModalMount } from '@petseo/shared';
import { FormModal } from '@/components/FormModal';
import { serviceSchema, type ServiceFormData, DURATION_OPTIONS } from '@/schemas/service.schema';
import { useCreateService, useUpdateService } from '../../hooks/api/services';
import type { ServiceVendorId, ServiceVendorServiceDto } from '@/services/api/petseo.client.wrapper';
import { createServiceVendorServiceId } from '@/services/api/petseo.client.wrapper';
import { formatServicePrice, getDurationOptions } from '@/utils/mappers';
import { IconCheck, IconEye, IconEyeOff, IconTag, IconTagOff, IconTrendingUp, type Icon } from '@tabler/icons-react';
import { pl } from '@/messages/pl';

interface ServiceFormProps {
  serviceVendorId: ServiceVendorId;
  service?: ServiceVendorServiceDto;
  isOpen: boolean;
  onClose: () => void;
}

const durationOptions = getDurationOptions(DURATION_OPTIONS);

type PriceMode = 'fixed' | 'from' | 'none';

const PRICE_MODES: { mode: PriceMode; label: string; icon: Icon }[] = [
  { mode: 'fixed', label: pl.serviceForm.priceModeFixed, icon: IconTag },
  { mode: 'from', label: pl.serviceForm.priceModeFrom, icon: IconTrendingUp },
  { mode: 'none', label: pl.serviceForm.priceModeNone, icon: IconTagOff },
];

function ServiceFormContent({ serviceVendorId, service, isOpen, onClose }: ServiceFormProps) {
  const isEditing = !!service;
  const createService = useCreateService();
  const updateService = useUpdateService();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    clearErrors,
  } = useForm<ServiceFormData>({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      name: '',
      durationMinutes: 30,
      isWithoutPrice: false,
      price: undefined,
      isPriceFrom: false,
    },
  });

  const isWithoutPrice = useWatch({ control, name: 'isWithoutPrice' });
  const isPriceFrom = useWatch({ control, name: 'isPriceFrom' });
  const price = useWatch({ control, name: 'price' });

  const priceMode: PriceMode = isWithoutPrice ? 'none' : isPriceFrom ? 'from' : 'fixed';
  const pricePreview = formatServicePrice(price, isPriceFrom);

  const handlePriceModeChange = (mode: PriceMode) => {
    setValue('isWithoutPrice', mode === 'none');
    setValue('isPriceFrom', mode === 'from');
    if (mode === 'none') clearErrors('price');
  };

  useEffect(() => {
    reset({
      name: service?.name ?? '',
      durationMinutes: service?.durationMinutes ?? 30,
      isWithoutPrice: service != null && service.price == null,
      price: service?.price ?? undefined,
      isPriceFrom: service?.isPriceFrom ?? false,
    });
  }, [isOpen, service, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async ({ name, durationMinutes, isWithoutPrice: withoutPrice, price, isPriceFrom }: ServiceFormData) => {
    const data = {
      name,
      durationMinutes,
      price: withoutPrice ? undefined : price,
      isPriceFrom: withoutPrice ? false : isPriceFrom,
    };

    if (isEditing && service?.id) {
      await updateService.mutateAsync({
        serviceVendorId,
        serviceVendorServiceId: createServiceVendorServiceId(service.id),
        data,
      });
    } else {
      await createService.mutateAsync({
        serviceVendorId,
        data,
      });
    }
    handleClose();
  };

  const isSubmitting = createService.isPending || updateService.isPending;
  const error = createService.error || updateService.error;

  return (
    <FormModal isOpen={isOpen} onClose={handleClose} title={isEditing ? pl.serviceForm.editTitle : pl.serviceForm.createTitle} size="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="modal-body">
          {error && <ErrorAlert apiError={error} />}

          <FormField
            control={control}
            name="name"
            label={pl.serviceForm.nameLabel}
            placeholder={pl.serviceForm.namePlaceholder}
            required
            error={errors.name}
          />

          <FormSelectField
            control={control}
            name="durationMinutes"
            label={pl.serviceForm.durationLabel}
            options={durationOptions}
            valueAsNumber
            required
            error={errors.durationMinutes}
          />

          <section className="mb-3" data-testid="service-price-section">
            <div className="form-label">{pl.serviceForm.priceModeLabel}</div>
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" role="tablist">
                  {PRICE_MODES.map(({ mode, label, icon: Icon }) => (
                    <li key={mode} className="nav-item" role="presentation">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={priceMode === mode}
                        className={`nav-link ${priceMode === mode ? 'active' : ''}`}
                        onClick={() => handlePriceModeChange(mode)}>
                        <Icon size={16} className="me-2" />
                        {label}
                        {priceMode === mode && <IconCheck size={16} className="ms-2 text-success" data-testid="price-mode-selected" />}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="card-body" role="tabpanel">
                {priceMode === 'none' ? (
                  <div className="d-flex align-items-center gap-2 px-3 py-2 rounded bg-secondary-lt" data-testid="service-price-preview">
                    <IconEyeOff size={18} className="flex-shrink-0" />
                    <span>{pl.serviceForm.withoutPriceDescription}</span>
                  </div>
                ) : (
                  <>
                    <FormField
                      control={control}
                      name="price"
                      label={pl.serviceForm.priceLabel}
                      labelClassName="visually-hidden"
                      wrapperClassName=""
                      type="number"
                      placeholder={pl.serviceForm.pricePlaceholder}
                      valueAsNumber
                      error={errors.price}
                    />
                    {pricePreview && (
                      <div className="d-flex align-items-center gap-2 mt-3 px-3 py-2 rounded bg-primary-lt" data-testid="service-price-preview">
                        <IconEye size={18} className="flex-shrink-0" />
                        <span>{pl.serviceForm.pricePreview}</span>
                        <strong className="fs-3">{pricePreview}</strong>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <FormCancelButton onClick={handleClose} disabled={isSubmitting} />
          <FormSubmitButton isLoading={isSubmitting} disabled={isSubmitting} className="btn btn-primary">
            {isSubmitting ? pl.serviceForm.submitting : pl.serviceForm.submit}
          </FormSubmitButton>
        </div>
      </form>
    </FormModal>
  );
}

export const ServiceForm = withModalMount(ServiceFormContent);
